import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { review as reviewAPI } from '../services/api';
import { usePageTracking } from '../hooks/usePageTracking';
import insta1 from '../assets/insta-1.png';
import insta2 from '../assets/insta-2.png';
import insta3 from '../assets/insta-3.png';
import insta4 from '../assets/insta-4.png';
import insta5 from '../assets/insta-5.png';
import insta6 from '../assets/insta-6.png';
import './Community.css';

/* ── fallback reviews (shown if API is unavailable) ── */
const FALLBACK_REVIEWS = [
  { name: 'Marcus T.', rating: 5, comment: 'This stuff hits different. Clean energy, no crash. I take it before every workout now.' },
  { name: 'Jess R.', rating: 5, comment: 'Finally an energy product that doesn\'t taste like chemicals. The candy is fire 🔥' },
  { name: 'Devon K.', rating: 4, comment: 'Great focus without the jitters. Way better than coffee for my morning runs.' },
  { name: 'Aaliyah M.', rating: 5, comment: 'Obsessed. I bought it for the gym but now I use it for everything — studying, work, you name it.' },
  { name: 'Chris P.', rating: 5, comment: 'My whole team switched to Sambas. The guarana really does last longer than regular caffeine.' },
  { name: 'Taylor S.', rating: 4, comment: 'Love the brand and the mission. Product tastes amazing and keeps me locked in.' },
  { name: 'Niko J.', rating: 5, comment: 'Best energy candy on the market, hands down. And the vitamin C is a nice bonus.' },
  { name: 'Priya D.', rating: 5, comment: 'I was skeptical but this really works. Smooth energy, great taste, clean ingredients.' },
];

/* ── Real Instagram posts from @sambas.energy ── */
/* To add real images: save screenshots from your IG posts into src/assets/
   and import them at the top like: import post1 from '../assets/insta-1.jpg'
   then set the image property below */
const INSTA_POSTS = [
  {
    id: 'DTrthTrFVn_',
    url: 'https://www.instagram.com/p/DTrthTrFVn_/',
    image: insta1,
    caption: 'Performance Candies - Coming 2026… Sign up now for pre-order.',
  },
  {
    id: 'DTrspYVlTX3',
    url: 'https://www.instagram.com/p/DTrspYVlTX3/',
    image: insta2,
    caption: 'Performance Candies - Coming 2026… Sign-up for pre-order now.',
  },
  {
    id: 'DSx2VEHEom8',
    url: 'https://www.instagram.com/p/DSx2VEHEom8/',
    image: insta3,
    caption: 'Taste Test + Review from SAMBAS Athlete @bretemeryy',
  },
  {
    id: 'DSx12lbkvBt',
    url: 'https://www.instagram.com/p/DSx12lbkvBt/',
    image: insta4,
    caption: 'Taste Test + Review from @_mmalachii 🔥🔥🔥',
  },
  {
    id: 'DSocvrpkfS9',
    url: 'https://www.instagram.com/p/DSocvrpkfS9/',
    image: insta5,
    caption: '*NEW* Naturally Caffeinated Performance Candies!',
  },
  {
    id: 'DSocgt1ESnU',
    url: 'https://www.instagram.com/p/DSocgt1ESnU/',
    image: insta6,
    caption: '*NEW* Naturally Caffeinated Performance Candies!',
  },
];

/* ── star helper ── */
function Stars({ count }) {
  return (
    <div className="community-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`star ${i <= count ? 'filled' : ''}`}>★</span>
      ))}
    </div>
  );
}

function Community() {
  usePageTracking('community');
  const navigate = useNavigate();
  const containerRef = useRef(null);

  /* reviews state */
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [loadingReviews, setLoadingReviews] = useState(true);

  /* review form state */
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewName, setReviewName] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);

  /* ── Scroll to top on mount ── */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /* ── Fetch reviews from API on mount ── */
  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await reviewAPI.getAll();
        if (res.success && res.data.length > 0) {
          setReviews(res.data);
        }
        // If no reviews in DB yet, keep fallback reviews
      } catch (err) {
        console.log('Using fallback reviews (API unavailable)');
      } finally {
        setLoadingReviews(false);
      }
    }
    fetchReviews();
  }, []);

  /* ── Scroll-triggered animations (same pattern as TheProduct) ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    const el = containerRef.current;
    if (el) {
      el.querySelectorAll('.animate-on-scroll').forEach((child) => {
        observer.observe(child);
      });
    }

    return () => observer.disconnect();
  }, [loadingReviews]); // Re-run after reviews load so new elements get observed

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0 || submitting) return;
    setSubmitError('');
    setSubmitting(true);

    try {
      const res = await reviewAPI.create(reviewName, rating, reviewComment);
      if (res.success) {
        setSubmitted(true);
        // Add the new review to the top of the list
        setReviews((prev) => [res.data, ...prev]);
        setTimeout(() => {
          setRating(0);
          setReviewName('');
          setReviewComment('');
          setSubmitted(false);
          setReviewOpen(false);
        }, 3000);
      }
    } catch (err) {
      setSubmitError('Failed to submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="community-page" ref={containerRef}>
      <BrandLogo />

      {/* Back button */}
      <button className="back-button-simple" onClick={() => navigate('/about')}>
        <span className="back-text">← Back</span>
        <span className="back-arrow">←</span>
      </button>

      <div className="community-container">

        {/* ── 1. HERO ── */}
        <div className="community-hero-wrap page-enter page-enter-1">
          <h1 className="community-hero">THANK YOU FOR<br />YOUR SUPPORT!</h1>
          <div className="hero-line" />
        </div>

        {/* ── 2. SCROLLABLE REVIEWS ── */}
        <div className="reviews-scroll-box animate-on-scroll">
          {reviews.map((r, idx) => (
            <div className="review-card" key={r._id || idx} style={{ animationDelay: `${idx * 0.08}s` }}>
              <Stars count={r.rating} />
              <p className="review-comment">"{r.comment}"</p>
              <span className="review-author">— {r.name}</span>
            </div>
          ))}
        </div>

        {/* ── 3. LEAVE A REVIEW ── */}
        <div className="review-form-section animate-on-scroll">
          <button
            className="review-toggle"
            onClick={() => setReviewOpen(!reviewOpen)}
          >
            Leave Us A Review
            <span className={`toggle-arrow ${reviewOpen ? 'open' : ''}`}>▾</span>
          </button>

          {reviewOpen && (
            <form className="review-form" onSubmit={handleSubmit}>
              {submitted ? (
                <p className="review-thanks">Thanks for your review! ✨</p>
              ) : (
                <>
                  {/* Stars */}
                  <div className="form-stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span
                        key={i}
                        className={`star interactive ${i <= (hoverRating || rating) ? 'filled' : ''}`}
                        onClick={() => setRating(i)}
                        onMouseEnter={() => setHoverRating(i)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        ★
                      </span>
                    ))}
                  </div>

                  {/* Name */}
                  <input
                    type="text"
                    className="review-input"
                    placeholder="Your name"
                    value={reviewName}
                    onChange={(e) => setReviewName(e.target.value)}
                    required
                  />

                  {/* Comment */}
                  <textarea
                    className="review-textarea"
                    placeholder="Write your review..."
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    rows={4}
                    required
                  />

                  {/* Submit */}
                  <button
                    type="submit"
                    className="review-submit"
                    disabled={rating === 0 || submitting}
                  >
                    {submitting ? 'SUBMITTING...' : 'SUBMIT'}
                  </button>
                  {submitError && <p className="review-error">{submitError}</p>}
                </>
              )}
            </form>
          )}
        </div>

        {/* ── 4. SOCIAL / HASHTAG SECTION ── */}
        <div className="community-social-section">
          <h2 className="community-hashtags animate-on-scroll">#SAMBASENERGY</h2>
          <h2 className="community-hashtags sub animate-on-scroll">#BETHEDIFFERENCE</h2>

          {/* Social icons */}
          <div className="social-icons animate-on-scroll">
            {/* Instagram */}
            <a href="https://www.instagram.com/sambas.energy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            {/* TikTok */}
            <a href="https://tiktok.com/@sambasenergy" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.87 2.87 0 0 1 .97.17v-3.5a6.37 6.37 0 0 0-.97-.07 6.34 6.34 0 0 0 0 12.68 6.34 6.34 0 0 0 6.34-6.34V9.41a8.16 8.16 0 0 0 3.76.92V6.69z" />
              </svg>
            </a>
            {/* Facebook */}
            <a href="https://facebook.com/sambasenergy" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
          </div>

          {/* 3×2 Instagram grid */}
          <div className="insta-grid animate-on-scroll">
            {INSTA_POSTS.map((post, idx) => (
              <a
                className="insta-tile"
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ animationDelay: `${idx * 0.07}s` }}
              >
                <div className="insta-tile-inner">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.caption}
                      className="insta-tile-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="insta-tile-placeholder">
                      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="insta-ph-icon">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                      <p className="insta-ph-caption">{post.caption}</p>
                    </div>
                  )}
                  {/* Caption always visible at bottom */}
                  {post.image && (
                    <div className="insta-tile-caption">
                      <p className="insta-caption-text">{post.caption}</p>
                    </div>
                  )}
                  {/* Icon on hover */}
                  <div className="insta-tile-overlay">
                    <span className="insta-overlay-icon">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Community;
